import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { supabase } from "@/integrations/supabase/client";
import { Send, Bot, User, Mic, MicOff, Sparkles, Anchor, Instagram, Copy, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import captainRocaLogo from '@/assets/captain-roca-logo.png';
import { TextToSpeech } from '@/components/TextToSpeech';
import { VoiceInput } from '@/components/VoiceInput';
// Email gate removed — public access

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CaptainRocaAIProps {
  userContext?: {
    name?: string;
    email?: string;
    userType?: string;
    tier?: string;
    position?: number;
  };
  onClose?: () => void;
}

interface CaptainRocaAIProps {
  userContext?: {
    name?: string;
    email?: string;
    userType?: string;
    tier?: string;
    position?: number;
  };
  onClose?: () => void;
  initialPrompt?: string;
}

export const CaptainRocaAI: React.FC<CaptainRocaAIProps> = ({ userContext, onClose, initialPrompt }) => {
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: `⚓ Welcome on Board, I'm Captain ROCA, your AI friend!\n\n"Happy by the sea, Action by Nature" - Let me help you to get to your destination.\n\nI'm here to help you identify your goals and give you practical next steps to get closer to your destination. Whether you're aspiring crew, filmmaker, recruiter, sponsor, or just curious - let's sail together!\n\n🌊 What brings you aboard TommyRoca.ai today?\n\n📱 Follow Tommy on Instagram @tommyrocaai for daily adventures!`,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check for email gate on mount
  useEffect(() => {
    const storedEmail = localStorage.getItem('captain_roca_email');
    const storedName = localStorage.getItem('captain_roca_name');
    if (!storedEmail && !userContext?.email) {
      setShowEmailGate(true);
    } else {
      setUserEmail(storedEmail || userContext?.email || '');
      setUserName(storedName || userContext?.name || '');
    }
  }, [userContext]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-send initialPrompt on mount (guard against StrictMode double-fire)
  const initialPromptSent = useRef(false);
  useEffect(() => {
    if (initialPrompt && !initialPromptSent.current) {
      initialPromptSent.current = true;
      sendMessage(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;

    // Track chat usage
    supabase.functions.invoke('create-analytics-event', {
      body: {
        event_type: 'captain_roca_chat_message',
        event_data: { 
          message_length: message.length,
          user_tier: userContext?.tier || 'unknown',
          timestamp: new Date().toISOString()
        }
      }
    });

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke('captain-roca-ai', {
        body: {
          message,
          userContext,
          conversationHistory
        }
      });

      if (error) throw error;

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      toast({
        title: "AI Error",
        description: "Captain ROCA is temporarily unavailable. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  const handleVoiceTranscript = (text: string) => {
    setInputMessage(text);
    // Optionally auto-send
    // sendMessage(text);
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({
      title: "Copied!",
      description: "Message copied to clipboard.",
    });
  };

  const shareConversation = () => {
    const conversationText = messages
      .map(m => `${m.role === 'user' ? 'You' : 'Captain ROCA'}: ${m.content}`)
      .join('\n\n');
    
    navigator.clipboard.writeText(conversationText);
    toast({
      title: "Conversation Copied!",
      description: "Share your Captain ROCA conversation with others.",
    });
  };

  const quickPrompts = [
    "I want to start diving",
    "I'm a casting director", 
    "Looking for crew training",
    "Support #DyslexicThinker"
  ];

  return (
    <Card className="w-full max-w-2xl mx-auto bg-gradient-to-br from-background/95 to-primary/5 border-primary/20 shadow-xl">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-foreground">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary/20">
              <img src={captainRocaLogo} alt="Captain ROCA" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">Captain ROCA AI</span>
                {userContext?.tier && (
                  <Badge variant="secondary" className="text-xs">
                    {userContext.tier.replace('_', ' ').toUpperCase()}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                <p>"Happy by the sea, Action by Nature"</p>
                <p>Let me help you to get to your destination</p>
              </div>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-primary/50 text-primary hover:bg-primary/20"
              onClick={shareConversation}
              disabled={messages.length <= 1}
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-primary/50 text-primary hover:bg-primary/20"
              onClick={() => window.open('https://instagram.com/tommyrocaai', '_blank')}
            >
              <Instagram className="w-4 h-4 mr-1" />
              Follow
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            AI Friend, Mentor & Guide • #DyslexicThinker Champion
          </p>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            Online
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Chat Messages */}
        <ScrollArea className="h-96 w-full border rounded-lg bg-muted/30 p-4">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start gap-3 ${
                  message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  message.role === 'user' 
                    ? 'bg-secondary' 
                    : 'bg-gradient-to-r from-blue-600 to-teal-500 overflow-hidden'
                }`}>
                  {message.role === 'user' ? (
                    <User className="w-4 h-4" />
                  ) : (
                    <img src={captainRocaLogo} alt="Captain ROCA" className="w-6 h-6 object-cover rounded-full" />
                  )}
                </div>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground ml-auto'
                    : 'bg-card border'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="text-xs opacity-60">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                    <div className="flex items-center gap-1">
                      {message.role === 'assistant' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyMessage(message.content)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <TextToSpeech 
                            text={message.content} 
                            voice="onyx"
                            className="h-6 w-6 p-1"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 flex items-center justify-center overflow-hidden">
                  <img src={captainRocaLogo} alt="Captain ROCA" className="w-6 h-6 object-cover rounded-full" />
                </div>
                <div className="bg-card border p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm">Captain ROCA is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </ScrollArea>

        {/* Quick Prompts */}
        <div className="grid grid-cols-2 gap-2">
          {quickPrompts.map((prompt, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              className="text-xs h-8"
              onClick={() => sendMessage(prompt)}
              disabled={isLoading}
            >
              {prompt}
            </Button>
          ))}
        </div>

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <VoiceInput 
            onTranscript={handleVoiceTranscript}
            language="en"
          />
          <Input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask Captain ROCA anything..."
            className="flex-1"
            disabled={isLoading}
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={isLoading || !inputMessage.trim()}
            className="bg-gradient-to-r from-primary to-blue-500"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>

        {/* Status */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            🔒 Secure AI Chat • Your data is protected • GDPR Compliant
          </p>
        </div>
      </CardContent>

      {/* Email gate removed — public access */}
    </Card>
  );
};